import { useRef, useState } from "react";
import type { SubRouteWithActive } from "@/components/dashboard/navigation";
import { useUserRoutes } from "@/components/dashboard/navigation";
import { SidebarNavItem } from "@/components/dashboard/sidebar-nav-item";
import { useNotifications } from "./providers/message-notification-provider";

type DashboardNavigationProps = {
  expanded: boolean;
  onNavigate?: () => void;
};

type UserNestedChoice = string | null | false;

function getEffectiveNestedId(
  expanded: boolean,
  userNestedId: UserNestedChoice,
  activeNestedRouteId: string | null
): string | null {
  if (!expanded) {
    return null;
  }
  if (userNestedId === false) {
    return activeNestedRouteId;
  }
  return userNestedId;
}

export function DashboardNavigation({
  expanded,
  onNavigate,
}: DashboardNavigationProps) {
  const routes = useUserRoutes();
  const { unreadConversationIds } = useNotifications();
  const [userNestedId, setUserNestedId] = useState<UserNestedChoice>(false);

  const activeNestedRouteId =
    routes.find((route) =>
      route.subs?.some((sub: SubRouteWithActive) => sub.isActive)
    )?.id ?? null;

  const prevExpandedRef = useRef(expanded);
  const prevActiveNestedRouteIdRef = useRef(activeNestedRouteId);

  if (expanded !== prevExpandedRef.current) {
    prevExpandedRef.current = expanded;
    if (!expanded) {
      setUserNestedId(false);
    }
  }

  if (activeNestedRouteId !== prevActiveNestedRouteIdRef.current) {
    prevActiveNestedRouteIdRef.current = activeNestedRouteId;
    setUserNestedId(false);
  }

  const effectiveNestedId = getEffectiveNestedId(
    expanded,
    userNestedId,
    activeNestedRouteId
  );

  return (
    <ul className="flex w-full flex-col gap-2">
      {routes.map((route) => {
        const hasUnread =
          route.id === "conversations" && unreadConversationIds.length > 0;

        return (
          <li key={route.id}>
            <SidebarNavItem
              expanded={expanded}
              hasUnread={hasUnread}
              isNestedOpen={effectiveNestedId === route.id}
              onNavigate={onNavigate}
              onToggleNested={() => {
                setUserNestedId(
                  effectiveNestedId === route.id ? null : route.id
                );
              }}
              route={route}
            />
          </li>
        );
      })}
    </ul>
  );
}
